// app/javascript/controllers/pages_controllers/gallery_carousel_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["track", "prevBtn", "nextBtn", "dot"]

  connect() {
    this.currentIndex = 0
    
    // Handle resize for responsive behavior
    if (this.resizeObserver) this.resizeObserver.disconnect()

    this.resizeObserver = new ResizeObserver(() => {
      this.updateSlideWidth()
      this.updateDots()
    })
    this.resizeObserver.observe(this.element)
    this.updateSlideWidth()
    this.updateButtons()
    this.updateDots()
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  updateSlideWidth() {
    // Get the number of visible slides based on screen width
    this.visibleSlides = this.getVisibleSlideCount()
    const containerWidth = this.trackTarget.parentElement.offsetWidth
    this.totalSlides = this.trackTarget.children.length
    
    // Calculate slide width based on container and visible count
    // Account for gaps between slides
    const gap = 16 // gap-4 = 1rem = 16px
    const totalGapWidth = gap * (this.visibleSlides - 1)
    this.slideWidth = (containerWidth - totalGapWidth) / this.visibleSlides
    
    // Set each slide's width explicitly
    Array.from(this.trackTarget.children).forEach(slide => {
      slide.style.width = `${this.slideWidth}px`
      slide.style.flexShrink = '0'
    })
    
    // Calculate max index (how many times we can slide)
    this.maxIndex = Math.max(0, this.totalSlides - this.visibleSlides)

    // Reset position if current index is out of bounds
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex
    }

    this.updatePosition()
    this.updateButtons()
  }

  getVisibleSlideCount() {
    const width = window.innerWidth
    if (width >= 1024) return 3 // lg
    if (width >= 640) return 2  // sm
    return 1
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++
      this.updatePosition()
      this.updateButtons()
      this.updateDots()
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.updatePosition()
      this.updateButtons()
      this.updateDots()
    }
  }

  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index >= 0 && index <= this.maxIndex) {
      this.currentIndex = index
      this.updatePosition()
      this.updateButtons()
      this.updateDots()
    }
  }

  updatePosition() {
    const gap = 16 // gap-4 = 1rem = 16px
    const offset = this.currentIndex * (this.slideWidth + gap)
    this.trackTarget.style.transform = `translateX(-${offset}px)`
  }

  updateButtons() {
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentIndex === 0
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.currentIndex >= this.maxIndex
    }
  }

  updateDots() {
    // Only show dots up to maxIndex (the number of "pages")
    this.dotTargets.forEach((dot, index) => {
      // Hide dots that are beyond maxIndex
      if (index > this.maxIndex) {
        dot.classList.add('hidden')
      } else {
        dot.classList.remove('hidden')
      }
      
      // Highlight current dot
      if (index === this.currentIndex) {
        dot.classList.remove('bg-gray-300')
        dot.classList.add('bg-ph24_blue')
      } else {
        dot.classList.remove('bg-ph24_blue')
        dot.classList.add('bg-gray-300')
      }
    })
  }
}
